import type {
  DoctorLabel,
  HealthLabel,
  NotMonitoredLabel,
  PlatformHealthVerdict,
} from "./types";

/**
 * One platform verdict with supporting evidence — operators should not have to
 * mentally correlate Health=Critical with Concierge Runtime=Critical tiles.
 *
 * Reason / evidence use only provided fields — never invent probe results.
 * Never surface generic "API" in operator-facing copy.
 */
export function derivePlatformHealthVerdict(input: {
  health: HealthLabel;
  doctor: DoctorLabel;
  /**
   * Concierge Runtime (tenant) health label — formerly misnamed "API".
   */
  conciergeRuntimeLabel?: HealthLabel | NotMonitoredLabel;
  /** @deprecated use conciergeRuntimeLabel */
  apiLabel?: HealthLabel | NotMonitoredLabel;
  /** Real doctor / operations message when present. */
  doctorMessage?: string | null;
  /** Real attention / incident problem text when present. */
  attentionProblem?: string | null;
}): PlatformHealthVerdict {
  const runtimeLabel = input.conciergeRuntimeLabel ?? input.apiLabel;
  const { health, doctor, doctorMessage, attentionProblem } = input;

  if (health === "Healthy" || health === "Unknown") {
    return { label: health, reason: null, evidence: null };
  }

  const reason = resolveReason({
    health,
    doctor,
    runtimeLabel,
    attentionProblem,
  });
  const evidence = resolveEvidence({ doctor, runtimeLabel, doctorMessage });

  return { label: health, reason, evidence };
}

function resolveReason(input: {
  health: HealthLabel;
  doctor: DoctorLabel;
  runtimeLabel?: HealthLabel | NotMonitoredLabel;
  attentionProblem?: string | null;
}): string | null {
  const problem = (input.attentionProblem ?? "").trim();
  if (problem) return problem;

  if (input.runtimeLabel === "Critical") return "Concierge Runtime unavailable";
  if (input.runtimeLabel === "Degraded") return "Concierge Runtime degraded";

  if (input.doctor === "Fail") return "Runtime Doctor reported failures";
  if (input.doctor === "Warn") return "Runtime Doctor reported warnings";

  if (input.health === "Critical") return "Platform health is Critical";
  if (input.health === "Degraded") return "Platform health is Degraded";
  return null;
}

function resolveEvidence(input: {
  doctor: DoctorLabel;
  runtimeLabel?: HealthLabel | NotMonitoredLabel;
  doctorMessage?: string | null;
}): string | null {
  const msg = (input.doctorMessage ?? "").trim();
  if (msg) return msg;

  if (input.doctor === "Fail") {
    if (input.runtimeLabel === "Critical") {
      return "Runtime Doctor failed Concierge Runtime health check";
    }
    return "Runtime Doctor: Fail";
  }
  if (input.doctor === "Warn") return "Runtime Doctor: Warn";
  if (input.runtimeLabel === "Critical") {
    return "Concierge Runtime health is Critical";
  }
  if (input.runtimeLabel === "Degraded") {
    return "Concierge Runtime health is Degraded";
  }
  return null;
}

/** Map pulse/runtime_doctor_status to HealthLabel. */
export function mapPlatformPulseStatus(
  status: string | null | undefined,
): HealthLabel {
  if (!status) return "Unknown";
  const s = String(status).trim().toLowerCase();
  if (s === "healthy" || s === "ok" || s === "green") return "Healthy";
  if (s === "degraded" || s === "yellow" || s === "warn") return "Degraded";
  if (s === "critical" || s === "unhealthy" || s === "red" || s === "fail") {
    return "Critical";
  }
  return "Unknown";
}
